import './EpcProjectsHeroArt.css';

/**
 * Engraved wind + solar scene for the EPC projects hero — same banknote
 * register as `components/branding/SoriginLogoBanner` (micro-hatch, rosette
 * medallion, engraved turbines with lime hub caps), recolored to sit on the
 * dark Sorigin-green hero gradient.
 */

interface TurbineProps {
  x: number;
  hubY: number;
  groundY: number;
  blade: number;
  spinClass: string;
}

function Turbine({ x, hubY, groundY, blade, spinClass }: TurbineProps) {
  const k = blade / 28;
  const halfBase = 1.6 * k;
  const halfTop = 0.45 * k;
  return (
    <g>
      {/* contact shadow */}
      <ellipse cx={x} cy={groundY} rx={11 * k} ry={1.3} fill="#06281D" fillOpacity="0.5" />

      {/* ground hatching */}
      <g stroke="#CDEBDB" strokeOpacity="0.35" strokeWidth="0.6" strokeLinecap="round">
        <line x1={x - 12 * k} y1={groundY + 3} x2={x + 12 * k} y2={groundY + 3} />
        <line x1={x - 16 * k} y1={groundY + 6} x2={x + 16 * k} y2={groundY + 6} strokeDasharray="1 2" />
        <line x1={x - 10 * k} y1={groundY + 9} x2={x + 10 * k} y2={groundY + 9} strokeDasharray="0.6 2" />
      </g>

      {/* tapered pole */}
      <path
        d={`M ${x - halfBase} ${groundY - 0.5} L ${x - halfTop} ${hubY + 1} L ${x + halfTop} ${hubY + 1} L ${x + halfBase} ${groundY - 0.5} Z`}
        fill="url(#eha-pole)"
      />

      {/* nacelle */}
      <path
        d={`M ${x - 4 * k} ${hubY + 3 * k} L ${x} ${hubY - 3 * k} L ${x + 4 * k} ${hubY + 3 * k} Z`}
        fill="#EAF6EF"
        fillOpacity="0.55"
      />
      <rect x={x - 2.5 * k} y={hubY - 1.5 * k} width={5 * k} height={3.5 * k} rx={0.6 * k} fill="#EAF6EF" fillOpacity="0.5" />

      {/* hub + blades */}
      <g transform={`translate(${x}, ${hubY})`}>
        <g className={spinClass}>
          <g transform={`scale(${k})`}>
            <path d="M 2.6 0 Q 12 -1.5 22 -0.6 Q 28 0 22 0.6 Q 12 1.5 2.6 0 Z" fill="#EAF6EF" fillOpacity="0.8" />
            <path d="M 2.6 0 Q 12 -1.5 22 -0.6 Q 28 0 22 0.6 Q 12 1.5 2.6 0 Z" fill="#EAF6EF" fillOpacity="0.8" transform="rotate(120)" />
            <path d="M 2.6 0 Q 12 -1.5 22 -0.6 Q 28 0 22 0.6 Q 12 1.5 2.6 0 Z" fill="#EAF6EF" fillOpacity="0.8" transform="rotate(240)" />
          </g>
        </g>
        <circle r={2.8 * k} fill="#A6CE39" />
        <circle r={2.8 * k} fill="none" stroke="#06281D" strokeOpacity="0.7" strokeWidth={0.5 * k} />
        <circle r={1 * k} fill="#06281D" fillOpacity="0.8" />
      </g>
    </g>
  );
}

interface SolarPanelProps {
  tx: number;
  ty: number;
  legY: number;
  clipId: string;
  gleamClass: string;
}

function SolarPanel({ tx, ty, legY, clipId, gleamClass }: SolarPanelProps) {
  return (
    <g transform={`translate(${tx}, ${ty})`}>
      {/* contact shadow */}
      <ellipse cx="36" cy={legY + 2} rx="30" ry="1.4" fill="#06281D" fillOpacity="0.5" />

      {/* legs */}
      <g stroke="#CDEBDB" strokeOpacity="0.65" strokeWidth="1.2" strokeLinecap="round">
        <line x1="12" y1="36" x2="14" y2={legY} />
        <line x1="60" y1="28" x2="62" y2={legY} />
        <line x1="14" y1={legY} x2="62" y2={legY} strokeOpacity="0.3" strokeWidth="0.7" />
      </g>

      {/* panel face */}
      <polygon points="0,8 60,0 68,30 8,38" fill="url(#eha-panel)" />
      <polygon points="0,8 60,0 68,30 8,38" fill="none" stroke="#DFF3E8" strokeOpacity="0.8" strokeWidth="0.9" strokeLinejoin="round" />

      {/* engraved cell grid */}
      <g stroke="#DFF3E8" strokeOpacity="0.45" strokeWidth="0.5">
        <line x1="20" y1="5.3" x2="28" y2="35.3" />
        <line x1="40" y1="2.7" x2="48" y2="32.7" />
        <line x1="4" y1="23" x2="64" y2="15" />
      </g>

      {/* gleam sweep */}
      <clipPath id={clipId}>
        <polygon points="0,8 60,0 68,30 8,38" />
      </clipPath>
      <g clipPath={`url(#${clipId})`}>
        <rect className={gleamClass} x="-24" y="-6" width="14" height="52" fill="#FFFFFF" fillOpacity="0.28" transform="skewX(-12)" />
      </g>
    </g>
  );
}

export default function EpcProjectsHeroArt({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 720 280"
      preserveAspectRatio="xMaxYMid slice"
      role="img"
      aria-hidden="true"
      className={`epc-hero-art ${className ?? ''}`}
    >
      <defs>
        <radialGradient id="eha-sun-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#A6CE39" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#A6CE39" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="eha-pole" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#EAF6EF" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#CFE9DC" stopOpacity="0.4" />
        </linearGradient>
        <linearGradient id="eha-panel" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#EAF6EF" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#EAF6EF" stopOpacity="0.08" />
        </linearGradient>
        <pattern id="eha-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="#EAF6EF" strokeOpacity="0.05" strokeWidth="0.4" />
        </pattern>
      </defs>

      {/* Layer 1 — diagonal micro-line hatch */}
      <rect width="720" height="280" fill="url(#eha-hatch)" />

      {/* Layer 2 — rosette medallion behind the lead turbine */}
      <g className="eha-rosette" transform="translate(566, 96)" fill="none" stroke="#DFF3E8" strokeWidth="0.5">
        <circle r="78" strokeOpacity="0.10" />
        <circle r="66" strokeOpacity="0.14" strokeDasharray="0.6 2" />
        <g strokeOpacity="0.08">
          {[0, 30, 60, 90, 120, 150].map((deg) => (
            <ellipse key={deg} rx="60" ry="34" transform={`rotate(${deg})`} />
          ))}
        </g>
        <circle r="46" strokeOpacity="0.12" />
        <circle r="24" strokeOpacity="0.08" />
      </g>

      {/* Layer 3 — engraved sun */}
      <g transform="translate(648, 56)">
        <circle r="44" fill="url(#eha-sun-glow)" />
        <circle r="17" fill="none" stroke="#A6CE39" strokeOpacity="0.75" strokeWidth="1" />
        <circle r="13" fill="none" stroke="#A6CE39" strokeOpacity="0.5" strokeWidth="0.5" strokeDasharray="1 2" />
        <circle className="eha-sun-core" r="11" fill="#A6CE39" fillOpacity="0.9" />
        <g stroke="#A6CE39" strokeWidth="1.6" strokeLinecap="round" strokeOpacity="0.8">
          {[...Array(12)].map((_, i) => {
            const a = (i * 30 * Math.PI) / 180;
            const r2 = i % 2 === 0 ? 34 : 29;
            return (
              <line
                key={i}
                x1={Math.cos(a) * 23}
                y1={Math.sin(a) * 23}
                x2={Math.cos(a) * r2}
                y2={Math.sin(a) * r2}
              />
            );
          })}
        </g>
      </g>

      {/* Layer 4 — wind flow curves */}
      <g fill="none" stroke="#BFE8D2" strokeWidth="1.6" strokeLinecap="round">
        <path d="M 16 132 Q 120 104, 224 132 T 432 132" strokeOpacity="0.5" />
        <path d="M 0 158 Q 120 130, 240 158 T 480 158" strokeOpacity="0.35" />
        <path d="M 32 184 Q 150 156, 268 184 T 504 184" strokeOpacity="0.22" />
      </g>

      {/* Layer 5 — birds */}
      <g fill="none" stroke="#DFF3E8" strokeOpacity="0.6" strokeWidth="1.1" strokeLinecap="round">
        <path d="M 348 68 q 5 -5 10 0 q 5 -5 10 0" />
        <path d="M 392 52 q 4 -4 8 0 q 4 -4 8 0" />
      </g>

      {/* Layer 6 — horizon */}
      <line x1="230" y1="244" x2="720" y2="244" stroke="#CDEBDB" strokeOpacity="0.3" strokeWidth="0.8" />

      {/* Layer 7 — solar array */}
      <SolarPanel tx={284} ty={194} legY={50} clipId="eha-clip-p1" gleamClass="eha-gleam" />
      <SolarPanel tx={362} ty={186} legY={58} clipId="eha-clip-p2" gleamClass="eha-gleam eha-gleam-delayed" />

      {/* Layer 8 — wind turbines (three depths) */}
      <Turbine x={448} hubY={150} groundY={244} blade={34} spinClass="eha-blades-b" />
      <Turbine x={678} hubY={168} groundY={244} blade={26} spinClass="eha-blades-c" />
      <Turbine x={566} hubY={96} groundY={244} blade={52} spinClass="eha-blades-a" />

      {/* Layer 9 — lime security dots */}
      <g fill="#A6CE39" fillOpacity="0.55">
        <circle cx="262" cy="60" r="0.9" />
        <circle cx="510" cy="40" r="0.7" />
        <circle cx="330" cy="120" r="0.7" />
        <circle cx="612" cy="200" r="0.9" />
        <circle cx="700" cy="120" r="0.7" />
      </g>

      {/* Layer 10 — serial-style corner marks */}
      <g fill="#DFF3E8" fillOpacity="0.3">
        <rect x="688" y="22" width="14" height="1" rx="0.5" />
        <rect x="693" y="24.5" width="9" height="0.8" rx="0.4" />
        <rect x="248" y="256" width="14" height="1" rx="0.5" />
        <rect x="248" y="258.5" width="9" height="0.8" rx="0.4" />
      </g>
    </svg>
  );
}
